import React, { useState, useMemo } from "react";
import {
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  IconButton,
  Grid,
  Tooltip,
  TextField,
  Switch,
  Box,
  CardHeader,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DesktopDateTimePicker } from "@mui/x-date-pickers/DesktopDateTimePicker";
import {
  Section,
  Campaign as CampaignType,
  CampaignState as CampaignStateType,
  Template,
  TemplateType,
  Team as TeamType,
  User,
  Response,
  Team,
  ItemType,
  ItemSubjectType,
  CampaignGradingMode,
  GroupCategory,
  ItemRecipientType,
  CampaignType as CampaignTypeEnum,
} from "../../types";
import { GridList, Hint } from "../MapList";
import { TemplateSelector } from "../Template/Selector";
import { CampaignState } from "./State";
import {
  BarChart,
  Close,
  LockOpen,
  MoreVert,
  Preview,
  Send,
  Check,
  Grading,
  Schedule,
  Download,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { CampaignProgress } from "./Progress";
import { useTranslation } from "react-i18next";
import { AssignmentSelector } from "../Assignment/Selector";
import { AssignmentGradingSelector } from "../AssignmentGrading/Selector";
import { GroupCategorySelector } from "../GroupCategory/Selector";
import { AssignmentDetails } from "../Assignment/Details";
import dayjs, { Dayjs } from "dayjs";
import advanced from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { RatingProportionSlider } from "../RatingProportion/Slider";
import {
  EditableTemplate,
  EditableAssignment,
  EditableGrading,
  EditableGroupCategory,
  EditableLoopStudent,
  EditableLoopInstructor,
  EditablePoints,
  EditableRatingProportion,
} from "./EditableFields";
import { GradePushModal } from "./GradePushModal";
import { ReleaseResultsConfirmModal } from "./ReleaseResultsConfirmModal";
import { Teams } from "./Teams";

dayjs.extend(advanced);
dayjs.extend(timezone);
dayjs.extend(utc);

export interface Props {
  campaign: CampaignType;
  categories: GroupCategory[];
  responses: Response[];
  section?: Section;
  sections: Section[];
  teams: TeamType[];
  template?: Template;
  templates: Template[];
  users: User[];
  onChange: (campaign: CampaignType, activityLabel: string) => void;
  onAssignmentChange: (campaign: CampaignType, activityLabel: string) => void;
  onDelete: (campaign: CampaignType) => void;
  onRemind: (campaign: CampaignType) => void;
  onExclude: (campaign: CampaignType, team: Team) => void;
  onSetGroupCategory: (
    campaign: CampaignType,
    groupcategory: GroupCategory | undefined,
  ) => void;
  onUpdateAssignmentPoints: (
    campaignId: string,
    assignmentPoints: string,
  ) => void;
  onPushGrades: () => void;
  isReleasing?: boolean;
  isPushingGrades?: boolean;
}

const Activity = ({ campaign }: { campaign: CampaignType }) => {
  return (
    <>
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
        Activity
      </Typography>
      <Card>
        <CardContent>
          <List>
            {campaign.activity.map((action) => (
              <ListItem key={action.id}>
                {dayjs(action.created).format("MM/DD/YYYY hh:mm A z")}
                :&nbsp;
                {action.label}
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </>
  );
};

export const CampaignEdit = ({
  campaign,
  categories,
  responses,
  teams,
  template,
  templates,
  users,
  onChange,
  onAssignmentChange,
  onDelete,
  onRemind,
  onExclude,
  onSetGroupCategory,
  onUpdateAssignmentPoints,
  onPushGrades,
  isReleasing = false,
  isPushingGrades = false,
}: Props) => {
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [closeDateOpen, setCloseDateOpen] = useState(false);
  const [sendDateOpen, setSendDateOpen] = useState(false);
  const [releaseDateOpen, setReleaseDateOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const actionMenuOpen = Boolean(anchorEl);
  const [t] = useTranslation();
  const theme = useTheme();
  const largerThanSm = useMediaQuery(theme.breakpoints.up("md"));
  const [gradePushNextAction, setGradePushNextAction] = useState<
    "send" | "schedule" | "push" | null
  >(null);
  const [pendingReleaseConfirm, setPendingReleaseConfirm] = useState<
    { kind: "manual" } | { kind: "scheduled"; date: Dayjs } | null
  >(null);

  const templateIds = templates.map((template) => template.id);
  const allTemplates =
    !template || templateIds.includes(template.id)
      ? templates
      : [template, ...templates];

  // Compute sendBlockReason reactively based on current campaign state
  // This updates immediately when template, teams, or assignment settings change
  const sendBlockReason = useMemo(() => {
    const noTemplate = !template;
    if (noTemplate) {
      return "Please select a template first.";
    }

    const hasNoTeams = teams.length === 0;
    if (hasNoTeams) {
      return "No teams have been assigned to this survey.";
    }

    const anyTeamHasNoMembers = teams.some((team) => team.userIds.length === 0);
    if (anyTeamHasNoMembers) {
      return "One or more teams have no members assigned to them.";
    }

    const hasAtLeastOneQuestion =
      !!template &&
      template.items.some(
        (item) =>
          item.itemType === ItemType.Choice || item.itemType === ItemType.Text,
      );
    if (!hasAtLeastOneQuestion) {
      return "Selected template must include at least one question.";
    }

    // Check if all teams have leads when required by template
    const allTeamsHaveLeads = teams.every((team) => team.leaderId !== null);
    const hasLeadQuestion =
      !!template &&
      template.items.some(
        (item) =>
          item.subjectType === ItemSubjectType.LeadOnly ||
          item.subjectType === ItemSubjectType.LeadSelf,
      );
    const hasTLQuestion =
      !!template &&
      template.items.some(
        (item) =>
          item.recipientType === ItemRecipientType.TeamLeaders ||
          item.recipientType === ItemRecipientType.NonLeaders,
      );

    if ((hasLeadQuestion || hasTLQuestion) && !allTeamsHaveLeads) {
      if (hasLeadQuestion) {
        return "Selected template includes questions targeting team leads but one or more teams have no leader assigned.";
      }
      if (hasTLQuestion) {
        return "Selected template includes questions targeting team leaders or non-leaders but one or more teams have no leader assigned.";
      }
    }

    // Check if all teams have instructors when required by template
    const allTeamsHaveInstructors = teams.every(
      (team) => team.hasInstructor === true,
    );
    const hasInstructorQuestion =
      !!template &&
      template.items.some(
        (item) => item.subjectType === ItemSubjectType.TeamInstructors,
      );
    const hasTIQuestion =
      !!template &&
      template.items.some(
        (item) => item.recipientType === ItemRecipientType.TeamInstructors,
      );

    if ((hasInstructorQuestion || hasTIQuestion) && !allTeamsHaveInstructors) {
      return "Selected template includes questions targeting team instructors but one or more teams have no instructor assigned.";
    }

    // Check if assignment points are set correctly
    if (
      campaign.assignmentEnabled &&
      (!campaign.assignmentPoints ||
        Number.parseInt(campaign.assignmentPoints) <= 0)
    ) {
      return "Assignment points must be set to a value greater than zero when assignments are enabled.";
    }

    // No blocking reason - survey can be sent
    return "";
  }, [template, teams, campaign.assignmentEnabled, campaign.assignmentPoints]);

  const isDeleteable = campaign.state === CampaignStateType.Draft;

  const tPrefix =
    campaign.campaignType === CampaignTypeEnum.Assignment
      ? "assignments"
      : "surveys";

  const handleActionClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleActionClose = () => {
    setAnchorEl(null);
  };

  function handleTemplateChange(template: Template) {
    onChange(
      { ...campaign, formId: template.id },
      t(`${tPrefix}.events.templateChange`, { template: template.name }),
    );
  }

  function handleAssignmentChange(assignmentEnabled: boolean) {
    const assignmentPoints = campaign.assignmentPoints || "10";
    onChange(
      { ...campaign, assignmentEnabled, assignmentPoints },
      t(`${tPrefix}.events.assignmentChange`, {
        assignment: assignmentEnabled ? "enabled" : "disabled",
      }),
    );
  }

  function handleAssignmentGradingChange(
    assignmentGradingMode: CampaignGradingMode,
  ) {
    onChange(
      { ...campaign, assignmentGradingMode },
      t(`${tPrefix}.events.assignmentGradingChange`, {
        assignmentGradingMode: assignmentGradingMode,
      }),
    );
  }

  function handleAssignmentPointsChange(assignmentPoints: string) {
    onChange(
      { ...campaign, assignmentPoints },
      t(`${tPrefix}.events.assignmentPointsChange`, {
        points: assignmentPoints,
      }),
    );
  }

  function handleAssignmentRatingProportionChange(value: number) {
    onChange(
      { ...campaign, assignmentRatingProportion: value },
      t(`${tPrefix}.events.assignmentRatingProportionChange`, {
        proportion: value,
      }),
    );
  }

  function handleDueDateChange(dueDate: Dayjs) {
    const updates: Partial<CampaignType> = {
      dueDate: dueDate.toISOString(),
    };

    // Keep 1-hour buffer for send date
    if (
      campaign.autoSendDate &&
      dueDate.isBefore(dayjs(campaign.autoSendDate))
    ) {
      updates.autoSendDate = dueDate.subtract(1, "hour").toISOString();
    }

    // Minimal spacing for close date
    if (
      campaign.autoCloseDate &&
      !dayjs(campaign.autoCloseDate).isAfter(dueDate)
    ) {
      updates.autoCloseDate = dueDate.add(1, "minute").toISOString();

      // Minimal spacing for release date
      if (
        campaign.autoReleaseDate &&
        !dayjs(campaign.autoReleaseDate).isAfter(dayjs(updates.autoCloseDate))
      ) {
        updates.autoReleaseDate = dayjs(updates.autoCloseDate)
          .add(1, "minute")
          .toISOString();
      }
    }

    onChange(
      {
        ...campaign,
        ...updates,
      },
      t(`${tPrefix}.events.dueDateChange`, {
        date: dueDate.toString(),
      }),
    );
  }

  function handleSendDateChange(sendDate: Dayjs | null) {
    const updates: Partial<CampaignType> = {
      autoSendDate: sendDate ? sendDate.toISOString() : null,
    };

    if (sendDate) {
      // Keep 1-hour buffer for due date
      if (campaign.dueDate && !dayjs(campaign.dueDate).isAfter(sendDate)) {
        updates.dueDate = sendDate.add(1, "hour").toISOString();

        // Minimal spacing for close date
        if (
          campaign.autoCloseDate &&
          !dayjs(campaign.autoCloseDate).isAfter(dayjs(updates.dueDate))
        ) {
          updates.autoCloseDate = dayjs(updates.dueDate)
            .add(1, "minute")
            .toISOString();

          // Minimal spacing for release date
          if (
            campaign.autoReleaseDate &&
            !dayjs(campaign.autoReleaseDate).isAfter(
              dayjs(updates.autoCloseDate),
            )
          ) {
            updates.autoReleaseDate = dayjs(updates.autoCloseDate)
              .add(1, "minute")
              .toISOString();
          }
        }
      }
    }

    onChange(
      {
        ...campaign,
        ...updates,
      },
      t(`${tPrefix}.events.sendDateChange`, {
        date: sendDate ? sendDate.toString() : "(disabled)",
      }),
    );
  }

  function handleCloseDateChange(closeDate: Dayjs | null) {
    const updates: Partial<CampaignType> = {
      autoCloseDate: closeDate ? closeDate.toISOString() : null,
    };

    if (closeDate) {
      // Minimal spacing for due date
      if (campaign.dueDate && dayjs(campaign.dueDate).isAfter(closeDate)) {
        updates.dueDate = closeDate.subtract(1, "minute").toISOString();

        // Keep 1-hour buffer between send and due date
        if (
          campaign.autoSendDate &&
          !dayjs(campaign.autoSendDate).isBefore(dayjs(updates.dueDate))
        ) {
          updates.autoSendDate = dayjs(updates.dueDate)
            .subtract(1, "hour")
            .toISOString();
        }
      }

      // Minimal spacing for release date
      if (
        campaign.autoReleaseDate &&
        !dayjs(campaign.autoReleaseDate).isAfter(closeDate)
      ) {
        updates.autoReleaseDate = closeDate.add(1, "minute").toISOString();
      }
    }

    onChange(
      {
        ...campaign,
        ...updates,
      },
      t(`${tPrefix}.events.closeDateChange`, {
        date: closeDate ? closeDate.toString() : "(disabled)",
      }),
    );
  }

  function handleReleaseDateChange(releaseDate: Dayjs | null) {
    // First-time scheduled release requires confirmation. Clearing the date or
    // changing an already-set date proceeds without prompting.
    if (releaseDate && !campaign.autoReleaseDate) {
      setPendingReleaseConfirm({ kind: "scheduled", date: releaseDate });
      return;
    }
    applyReleaseDateChange(releaseDate);
  }

  function applyReleaseDateChange(releaseDate: Dayjs | null) {
    const updates: Partial<CampaignType> = {
      autoReleaseDate: releaseDate ? releaseDate.toISOString() : null,
    };

    if (releaseDate) {
      // Minimal spacing for close date
      if (
        campaign.autoCloseDate &&
        dayjs(campaign.autoCloseDate).isAfter(releaseDate)
      ) {
        updates.autoCloseDate = releaseDate.subtract(1, "minute").toISOString();

        // Minimal spacing for due date
        if (
          campaign.dueDate &&
          dayjs(campaign.dueDate).isAfter(dayjs(updates.autoCloseDate))
        ) {
          updates.dueDate = dayjs(updates.autoCloseDate)
            .subtract(1, "minute")
            .toISOString();

          // Keep 1-hour buffer between send and due date
          if (
            campaign.autoSendDate &&
            !dayjs(campaign.autoSendDate).isBefore(dayjs(updates.dueDate))
          ) {
            updates.autoSendDate = dayjs(updates.dueDate)
              .subtract(1, "hour")
              .toISOString();
          }
        }
      }
    }

    onChange(
      {
        ...campaign,
        ...updates,
      },
      t(`${tPrefix}.events.releaseDateChange`, {
        date: releaseDate ? releaseDate.toString() : "(disabled)",
      }),
    );
  }

  function doSend() {
    // Determine the correct next state based on current state and campaign type
    let newState: CampaignStateType;

    if (campaign.state === CampaignStateType.AssignmentClosed) {
      // When in AssignmentClosed state, "Send Review" should go to Active (peer review phase)
      newState = CampaignStateType.Active;
    } else if (campaign.campaignType === CampaignTypeEnum.Assignment) {
      // For Live Presentation assignments, bypass assignment step and go straight to Active
      const isLivePresentation = campaign.assignment?.submissionType === "LIV";
      if (isLivePresentation) {
        newState = CampaignStateType.Active;
      } else {
        // For assignment campaigns in Draft state, go to AssignmentActive
        newState = CampaignStateType.AssignmentActive;
      }
    } else {
      // For survey campaigns, go directly to Active
      newState = CampaignStateType.Active;
    }

    onChange({ ...campaign, state: newState }, t(`${tPrefix}.events.send`));
  }

  function handleSendClick() {
    const course = campaign.course as any;
    if (
      campaign.assignmentEnabled &&
      course?.gradePushCapable &&
      !course?.gradePushReady
    ) {
      setGradePushNextAction("send");
      setShowGradePushDialog(true);
      return;
    }
    doSend();
  }

  function doSchedule() {
    // For assignment campaigns from Draft, transition to AssignmentScheduled
    // For survey campaigns, transition to Scheduled
    const newState =
      campaign.campaignType === CampaignTypeEnum.Assignment
        ? CampaignStateType.AssignmentScheduled
        : CampaignStateType.Scheduled;

    onChange({ ...campaign, state: newState }, t(`${tPrefix}.events.schedule`));
  }

  function handleScheduleClick() {
    const course = campaign.course as any;
    if (
      campaign.assignmentEnabled &&
      course?.gradePushCapable &&
      !course?.gradePushReady
    ) {
      setGradePushNextAction("schedule");
      setShowGradePushDialog(true);
      return;
    }
    doSchedule();
  }

  function handleAssignmentActivateClick() {
    onChange(
      { ...campaign, state: CampaignStateType.AssignmentActive },
      t(`${tPrefix}.events.assignmentActivate`),
    );
  }

  function handleAssignmentCloseClick() {
    onChange(
      { ...campaign, state: CampaignStateType.AssignmentClosed },
      t(`${tPrefix}.events.assignmentClose`),
    );
  }

  function handleScheduleReviewClick() {
    onChange(
      { ...campaign, state: CampaignStateType.Scheduled },
      t(`${tPrefix}.events.scheduleReview`),
    );
  }

  function handleReminderClick() {
    onRemind(campaign);
  }

  function handleDeleteClick() {
    onDelete(campaign);
  }

  function setCampaignTitle(label: string) {
    if (label === campaign.name) {
      return;
    }
    onChange(
      { ...campaign, name: label },
      `Name Changed from ${campaign.name} to ${label}`,
    );
  }

  function handleCloseClick() {
    onChange(
      { ...campaign, state: CampaignStateType.Closed },
      t(`${tPrefix}.events.close`),
    );
  }

  function handleReopenClick() {
    onChange(
      { ...campaign, state: CampaignStateType.Active },
      t(`${tPrefix}.events.reopen`),
    );
  }

  function handleReleaseClick() {
    setPendingReleaseConfirm({ kind: "manual" });
  }

  function doRelease() {
    onChange(
      { ...campaign, released: new Date().toISOString() },
      t(`${tPrefix}.events.release`),
    );
  }

  function handleUnreleaseClick() {
    onChange(
      { ...campaign, released: null },
      t(`${tPrefix}.menu.unreleaseResults`),
    );
  }

  function handleSetGroupCategoryClick(
    groupcategory: GroupCategory | undefined,
  ) {
    onSetGroupCategory(campaign, groupcategory);
  }

  const [showGradePushDialog, setShowGradePushDialog] = useState(false);

  function handlePushGradesClick() {
    const course = campaign.course as any;
    if (course?.gradePushCapable && !course?.gradePushReady) {
      setGradePushNextAction("push");
      setShowGradePushDialog(true);
      return;
    }
    onPushGrades();
  }

  async function handleExportGradesClick() {
    // try {
    //   const resp = await fetch(`/api/campaigns/${campaign.id}/grades.csv`, {
    //     credentials: "include",
    //   });
    //   if (!resp.ok) {
    //     throw new Error(await resp.text());
    //   }
    //   const blob = await resp.blob();
    //   const url = window.URL.createObjectURL(blob);
    //   const a = document.createElement("a");
    //   a.href = url;
    //   a.download = `campaign-${campaign.id}-grades.csv`;
    //   document.body.appendChild(a);
    //   a.click();
    //   a.remove();
    //   window.URL.revokeObjectURL(url);
    // } catch (e) {
    //   console.error(e);
    // }
    window.location.href = `/api/campaigns/${campaign.id}/grades.csv`;
  }

  function handleCancelClick() {
    onChange(
      {
        ...campaign,
        state: CampaignStateType.Draft,
        autoSendDate: null,
      },
      t(`${tPrefix}.events.cancelSchedule`),
    );
  }

  function EditContent() {
    const baseItems = [
      {
        label: t("courses.course"),
        value: campaign.course.name,
      },
      // TEMPORARILY REMOVED for 337-multi-section-support
      // {
      //   label: t("sections.section"),
      //   value: (
      //     <SectionSelector
      //       sections={sections}
      //       selected={section}
      //       onChange={(section) => handleSectionChange(section)}
      //     />
      //   ),
      // },
      {
        label:
          campaign.campaignType === CampaignTypeEnum.Assignment
            ? t("templates.rubric")
            : t("templates.template"),
        value: (
          <TemplateSelector
            templates={allTemplates}
            selected={template}
            onChange={(template) => handleTemplateChange(template)}
            templateType={
              campaign.campaignType === CampaignTypeEnum.Assignment
                ? TemplateType.Rubric
                : TemplateType.Survey
            }
          />
        ),
      },
      {
        label: t(`${tPrefix}.dueDate`),
        value: (
          <DesktopDateTimePicker
            open={dueDateOpen}
            onOpen={() => setDueDateOpen(true)}
            onClose={() => setDueDateOpen(false)}
            label={t(`${tPrefix}.dueDate`)}
            value={campaign.dueDate ? dayjs(campaign.dueDate) : undefined}
            onAccept={(date) => {
              if (!date) return;
              handleDueDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setDueDateOpen(true)}
                />
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
            }}
          />
        ),
      },
      {
        label: t(`${tPrefix}.autoSendDate`),
        value: (
          <DesktopDateTimePicker
            open={sendDateOpen}
            onOpen={() => setSendDateOpen(true)}
            onClose={() => setSendDateOpen(false)}
            // label={t(`${tPrefix}.autoEmptyDate`)}
            value={
              campaign.autoSendDate ? dayjs(campaign.autoSendDate) : undefined
            }
            onAccept={(date) => {
              handleSendDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setSendDateOpen(true)}
                />
              ),
              clearButton: () => (
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleSendDateChange(null);
                  }}
                >
                  <Close />
                </IconButton>
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
              field: { clearable: true },
            }}
          />
        ),
      },
      {
        label: t(`${tPrefix}.autoCloseDate`),
        value: (
          <DesktopDateTimePicker
            open={closeDateOpen}
            onOpen={() => setCloseDateOpen(true)}
            onClose={() => setCloseDateOpen(false)}
            // label={t(`${tPrefix}.autoCloseDate`)}
            value={
              campaign.autoCloseDate ? dayjs(campaign.autoCloseDate) : undefined
            }
            onAccept={(date) => {
              handleCloseDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setCloseDateOpen(true)}
                />
              ),
              clearButton: () => (
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleCloseDateChange(null);
                  }}
                >
                  <Close />
                </IconButton>
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
              field: { clearable: true },
            }}
          />
        ),
      },
      {
        label: t(`${tPrefix}.autoReleaseDate`),
        value: (
          <DesktopDateTimePicker
            open={releaseDateOpen}
            onOpen={() => setReleaseDateOpen(true)}
            onClose={() => setReleaseDateOpen(false)}
            // label={t(`${tPrefix}.autoReleaseDate`)}
            value={
              campaign.autoReleaseDate
                ? dayjs(campaign.autoReleaseDate)
                : undefined
            }
            onAccept={(date) => {
              handleReleaseDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setReleaseDateOpen(true)}
                />
              ),
              clearButton: () => (
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleReleaseDateChange(null);
                  }}
                >
                  <Close />
                </IconButton>
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
              field: { clearable: true },
            }}
          />
        ),
      },
      {
        label: t("assignments.selector"),
        labelId: "assignment-select-label",
        value: (
          <AssignmentSelector
            selected={campaign.assignmentEnabled}
            onChange={(enabled) => handleAssignmentChange(enabled)}
          />
        ),
      },
      {
        label: t("assignmentsGrading.selector"),
        labelId: "assignment-grading-select-label",
        value: campaign.assignmentEnabled ? (
          <AssignmentGradingSelector
            selected={campaign.assignmentGradingMode}
            onChange={(gradingMode) =>
              handleAssignmentGradingChange(gradingMode)
            }
          />
        ) : (
          "n/a"
        ),
      },
      {
        label: t("assignments.ratingProportion"),
        value:
          campaign.assignmentEnabled &&
          campaign.assignmentGradingMode === "FA" ? (
            <RatingProportionSlider
              initialValue={campaign.assignmentRatingProportion || 0}
              onChangeCommitted={(value) =>
                handleAssignmentRatingProportionChange(value)
              }
            />
          ) : (
            "n/a"
          ),
      },
      {
        label: t("assignments.points"),
        value: campaign.assignmentEnabled ? (
          <TextField
            size="small"
            fullWidth
            defaultValue={campaign.assignmentPoints || ""}
            onBlur={(e) => handleAssignmentPointsChange(e.target.value)}
            type="number"
          />
        ) : (
          "n/a"
        ),
      },
      {
        label: t(`${tPrefix}.loopStudent`),
        value: (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box component="span">No</Box>
            <Switch
              checked={campaign.loopStudent}
              onChange={(e) => {
                onChange(
                  { ...campaign, loopStudent: e.target.checked },
                  t(`${tPrefix}.events.loopStudentChange`, {
                    enabled: e.target.checked ? "enabled" : "disabled",
                  }),
                );
              }}
            />
            <Box component="span">Yes</Box>
            <Hint toolTip={t(`${tPrefix}.loopStudentHint`)} />
          </Box>
        ),
      },
      {
        label: t(`${tPrefix}.loopInstructor`),
        value: (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box component="span">No</Box>
            <Switch
              checked={campaign.loopInstructor}
              onChange={(e) => {
                onChange(
                  { ...campaign, loopInstructor: e.target.checked },
                  t(`${tPrefix}.events.loopInstructorChange`, {
                    enabled: e.target.checked ? "enabled" : "disabled",
                  }),
                );
              }}
            />
            <Box component="span">Yes</Box>
            <Hint toolTip={t(`${tPrefix}.loopInstructorHint`)} />
          </Box>
        ),
      },
    ];

    // Add Group Category and Teams Count fields only for non-assignment campaigns
    // or when not in the two-column layout
    const additionalItems =
      campaign.campaignType !== CampaignTypeEnum.Assignment
        ? [
            {
              label: "Select group category",
              labelId: "category-select-label",
              value: (
                <GroupCategorySelector
                  categories={categories}
                  selected={campaign.groupcategory}
                  onChange={(groupcategory) =>
                    handleSetGroupCategoryClick(groupcategory)
                  }
                />
              ),
            },
            {
              label: t(`${tPrefix}.teamCount`),
              value: campaign.groups.length,
            },
          ]
        : [];

    return <GridList items={[...baseItems, ...additionalItems]} />;
  }

  function ViewContent() {
    const now = dayjs();
    const canEditSendDate =
      campaign.state === CampaignStateType.Scheduled ||
      (campaign.autoSendDate && dayjs(campaign.autoSendDate).isAfter(now));
    const canEditCloseDate =
      campaign.state !== CampaignStateType.Closed &&
      (!campaign.autoCloseDate || dayjs(campaign.autoCloseDate).isAfter(now));
    const canEditReleaseDate =
      !campaign.released &&
      (!campaign.autoReleaseDate ||
        dayjs(campaign.autoReleaseDate).isAfter(now));

    const baseItems = [
      {
        label: t("courses.course"),
        value: campaign.course.name,
      },
      // {
      //   label: t("sections.section"),
      //   value: section ? section.name : "All sections",
      // },
      {
        label:
          campaign.campaignType === CampaignTypeEnum.Assignment
            ? t("templates.rubric")
            : t("templates.template"),
        value: (
          <EditableTemplate
            campaign={campaign}
            template={template}
            templates={templates}
            onChange={onChange}
          />
        ),
      },
      {
        label: t(`${tPrefix}.autoSendDate`),
        value: canEditSendDate ? (
          <DesktopDateTimePicker
            open={sendDateOpen}
            onOpen={() => setSendDateOpen(true)}
            onClose={() => setSendDateOpen(false)}
            // label={t(`${tPrefix}.autoSendDate`)}
            value={
              campaign.autoSendDate ? dayjs(campaign.autoSendDate) : undefined
            }
            onAccept={(date) => {
              if (!date) return;
              handleSendDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setSendDateOpen(true)}
                />
              ),
              clearButton: () => (
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleSendDateChange(null);
                  }}
                >
                  <Close />
                </IconButton>
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
              field: { clearable: true },
            }}
          />
        ) : campaign.autoSendDate ? (
          dayjs(campaign.autoSendDate).format("M/DD/YYYY h:mm A z")
        ) : (
          "Not scheduled"
        ),
      },
      {
        label: t(`${tPrefix}.dueDate`),
        value: dayjs(campaign.dueDate).format("M/DD/YYYY h:mm A z"),
      },
      {
        label: t(`${tPrefix}.autoCloseDate`),
        value: canEditCloseDate ? (
          <DesktopDateTimePicker
            open={closeDateOpen}
            onOpen={() => setCloseDateOpen(true)}
            onClose={() => setCloseDateOpen(false)}
            // label={t(`${tPrefix}.autoCloseDate`)}
            value={
              campaign.autoCloseDate ? dayjs(campaign.autoCloseDate) : undefined
            }
            onAccept={(date) => {
              if (!date) return;
              handleCloseDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setCloseDateOpen(true)}
                />
              ),
              clearButton: () => (
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleCloseDateChange(null);
                  }}
                >
                  <Close />
                </IconButton>
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
              field: { clearable: true },
            }}
          />
        ) : campaign.autoCloseDate ? (
          dayjs(campaign.autoCloseDate).format("M/DD/YYYY h:mm A z")
        ) : (
          "Not scheduled"
        ),
      },
      {
        label: t(`${tPrefix}.autoReleaseDate`),
        value: canEditReleaseDate ? (
          <DesktopDateTimePicker
            open={releaseDateOpen}
            onOpen={() => setReleaseDateOpen(true)}
            onClose={() => setReleaseDateOpen(false)}
            // label={t(`${tPrefix}.autoReleaseDate`)}
            value={
              campaign.autoReleaseDate
                ? dayjs(campaign.autoReleaseDate)
                : undefined
            }
            onAccept={(date) => {
              if (!date) return;
              handleReleaseDateChange(date);
            }}
            slots={{
              textField: (params) => (
                <TextField
                  size="small"
                  fullWidth
                  {...params}
                  onClick={() => setReleaseDateOpen(true)}
                />
              ),
              clearButton: () => (
                <IconButton
                  onClick={(evt) => {
                    evt.stopPropagation();
                    handleReleaseDateChange(null);
                  }}
                >
                  <Close />
                </IconButton>
              ),
            }}
            slotProps={{
              layout: {
                sx: {
                  ul: {
                    "::-webkit-scrollbar": {
                      width: "2px",
                    },
                  },
                },
              },
              field: { clearable: true },
            }}
          />
        ) : campaign.autoReleaseDate ? (
          dayjs(campaign.autoReleaseDate).format("M/DD/YYYY h:mm A z")
        ) : (
          "Not scheduled"
        ),
      },
      {
        label: t("assignments.selector"),
        value: (
          <EditableAssignment
            key="assignment"
            campaign={campaign}
            onChange={onAssignmentChange}
          />
        ),
      },
      {
        label: t("assignmentsGrading.selector"),
        value: <EditableGrading campaign={campaign} onChange={onChange} />,
      },
      {
        label: t("assignments.ratingProportion"),
        value: (
          <EditableRatingProportion campaign={campaign} onChange={onChange} />
        ),
      },
      {
        label: t("assignments.points"),
        value: (
          <EditablePoints
            campaign={campaign}
            onUpdatePoints={onUpdateAssignmentPoints}
          />
        ),
      },
      {
        label: t(`${tPrefix}.loopStudent`),
        value: (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <EditableLoopStudent campaign={campaign} onChange={onChange} />
            <Hint toolTip={t(`${tPrefix}.loopStudentHint`)} />
          </Box>
        ),
      },
      {
        label: t(`${tPrefix}.loopInstructor`),
        value: (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <EditableLoopInstructor campaign={campaign} onChange={onChange} />
            <Hint toolTip={t(`${tPrefix}.loopInstructorHint`)} />
          </Box>
        ),
      },
    ];

    // Add Group Category and Teams Count fields only for non-assignment campaigns
    const additionalItems =
      campaign.campaignType !== CampaignTypeEnum.Assignment
        ? [
            {
              label: t(`${tPrefix}.teamSet`),
              value: (
                <EditableGroupCategory
                  campaign={campaign}
                  categories={categories}
                  onSetGroupCategory={onSetGroupCategory}
                />
              ),
            },
            {
              label: t(`${tPrefix}.teamCount`),
              value: campaign.groups.length,
            },
          ]
        : [];

    return <GridList items={[...baseItems, ...additionalItems]} />;
  }

  return (
    <>
      {pendingReleaseConfirm && (
        <ReleaseResultsConfirmModal
          onCancel={() => setPendingReleaseConfirm(null)}
          onConfirm={() => {
            const pending = pendingReleaseConfirm;
            setPendingReleaseConfirm(null);
            if (pending.kind === "manual") {
              doRelease();
            } else {
              applyReleaseDateChange(pending.date);
            }
          }}
        />
      )}
      {showGradePushDialog && (
        <GradePushModal
          course={campaign.course as any}
          context={gradePushNextAction || "push"}
          onClose={() => {
            setShowGradePushDialog(false);
            setGradePushNextAction(null);
          }}
          onSkip={() => {
            if (gradePushNextAction === "push") {
              onPushGrades();
            } else if (gradePushNextAction === "send") {
              doSend();
            } else if (gradePushNextAction === "schedule") {
              doSchedule();
            }
            setShowGradePushDialog(false);
            setGradePushNextAction(null);
          }}
          onConnected={() => {
            if (gradePushNextAction === "push") {
              onPushGrades();
            } else if (gradePushNextAction === "send") {
              doSend();
            } else if (gradePushNextAction === "schedule") {
              doSchedule();
            }
            setShowGradePushDialog(false);
            setGradePushNextAction(null);
          }}
        />
      )}
      <Card>
        <CardHeader
          action={
            <>
              <CampaignState sx={{ ml: 2 }} campaign={campaign} />
              <span>&nbsp;</span>
              {largerThanSm ? (
                <>
                  {campaign.state !== CampaignStateType.Draft && (
                    <Tooltip title="View All Results">
                      <IconButton
                        component={Link}
                        to={`/results/${campaign.id}//`}
                        sx={{ m: 1 }}
                        color="success"
                      >
                        <BarChart />
                      </IconButton>
                    </Tooltip>
                  )}
                  {campaign.state === CampaignStateType.Closed &&
                    (campaign.released === null ? (
                      <Tooltip
                        title={
                          campaign.autoReleaseDate
                            ? "Release Results Now"
                            : "Release Results"
                        }
                      >
                        <IconButton
                          sx={{ m: 1 }}
                          color="success"
                          onClick={handleReleaseClick}
                          disabled={isReleasing}
                        >
                          {isReleasing ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Preview />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Results Released">
                        <span>
                          <IconButton disabled sx={{ m: 1 }}>
                            <Check />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ))}
                  {campaign.state === CampaignStateType.Closed &&
                    campaign.assignmentEnabled &&
                    // When native sync is ready, hide the prominent icon and use menu item instead
                    !(
                      (campaign.course as any)?.gradePushCapable &&
                      (campaign.course as any)?.gradePushReady
                    ) && (
                      <Tooltip
                        title={
                          campaign.gradesSyncedToLms
                            ? `Push grades to LMS (last pushed ${dayjs(
                                campaign.gradesSyncedToLms,
                              ).format("MM/DD/YYYY hh:mm A z")})`
                            : "Push Grades to LMS"
                        }
                      >
                        <IconButton
                          sx={{ m: 1 }}
                          color="success"
                          onClick={handlePushGradesClick}
                          disabled={isPushingGrades}
                        >
                          {isPushingGrades ? (
                            <CircularProgress size={24} />
                          ) : campaign.gradesSyncedToLms ? (
                            <Check />
                          ) : (
                            <Grading />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}

                  {(campaign.state === CampaignStateType.Draft ||
                    campaign.state === CampaignStateType.AssignmentClosed) &&
                    (sendBlockReason ? (
                      <Tooltip title={sendBlockReason}>
                        <span>
                          {campaign.autoSendDate ? (
                            <Button
                              variant="contained"
                              sx={{ m: 1 }}
                              endIcon={<Send />}
                              onClick={handleScheduleClick}
                              disabled
                            >
                              Schedule
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              sx={{ m: 1 }}
                              endIcon={<Send />}
                              onClick={handleSendClick}
                              disabled
                            >
                              {campaign.state ===
                              CampaignStateType.AssignmentClosed
                                ? "Send Review"
                                : "Send"}
                            </Button>
                          )}
                        </span>
                      </Tooltip>
                    ) : campaign.autoSendDate &&
                      campaign.state === CampaignStateType.Draft ? (
                      <Button
                        variant="contained"
                        sx={{ m: 1 }}
                        endIcon={<Send />}
                        onClick={handleScheduleClick}
                      >
                        Schedule
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        sx={{ m: 1 }}
                        endIcon={<Send />}
                        onClick={handleSendClick}
                      >
                        {campaign.state === CampaignStateType.AssignmentClosed
                          ? "Send Review"
                          : "Send"}
                      </Button>
                    ))}

                  {campaign.state !== CampaignStateType.Draft &&
                    campaign.state !== CampaignStateType.Closed &&
                    campaign.state !== CampaignStateType.Scheduled &&
                    campaign.state !== CampaignStateType.AssignmentScheduled &&
                    campaign.state !== CampaignStateType.AssignmentClosed && (
                      <>
                        {campaign.state ===
                        CampaignStateType.AssignmentActive ? (
                          <Button
                            variant="contained"
                            sx={{ m: 1 }}
                            endIcon={<Close />}
                            onClick={handleAssignmentCloseClick}
                          >
                            Close Assignment
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            sx={{ m: 1 }}
                            endIcon={<Close />}
                            onClick={handleCloseClick}
                          >
                            Close Survey
                          </Button>
                        )}
                      </>
                    )}
                  <IconButton
                    onClick={handleActionClick}
                    size="small"
                    sx={{ ml: 2 }}
                    aria-controls={actionMenuOpen ? "campaign-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={actionMenuOpen ? "true" : undefined}
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={actionMenuOpen}
                    onClose={handleActionClose}
                    onClick={handleActionClose}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        overflow: "visible",
                        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                        mt: 1.5,
                        "& .MuiAvatar-root": {
                          width: 32,
                          height: 32,
                          ml: -0.5,
                          mr: 1,
                        },
                        "&:before": {
                          content: '""',
                          display: "block",
                          position: "absolute",
                          top: 0,
                          right: 14,
                          width: 10,
                          height: 10,
                          bgcolor: "background.paper",
                          transform: "translateY(-50%) rotate(45deg)",
                          zIndex: 0,
                        },
                      },
                    }}
                    transformOrigin={{
                      horizontal: "right",
                      vertical: "top",
                    }}
                    anchorOrigin={{
                      horizontal: "right",
                      vertical: "bottom",
                    }}
                  >
                    {[
                      CampaignStateType.Draft,
                      CampaignStateType.AssignmentClosed,
                    ].includes(campaign.state) &&
                      campaign.campaignType === CampaignTypeEnum.Assignment &&
                      !campaign.assignment?.assignmentSendDate &&
                      (sendBlockReason ? (
                        <Tooltip title={sendBlockReason}>
                          <MenuItem disabled>
                            <ListItemIcon>
                              <Send fontSize="small" />
                            </ListItemIcon>
                            {campaign.state ===
                            CampaignStateType.AssignmentClosed
                              ? "Send Review"
                              : "Send Assignment"}
                          </MenuItem>
                        </Tooltip>
                      ) : (
                        <MenuItem onClick={handleSendClick}>
                          <ListItemIcon>
                            <Send fontSize="small" />
                          </ListItemIcon>
                          {campaign.state === CampaignStateType.AssignmentClosed
                            ? "Send Review"
                            : "Send Assignment"}
                        </MenuItem>
                      ))}
                    {campaign.state === CampaignStateType.Draft &&
                      campaign.campaignType === CampaignTypeEnum.Assignment &&
                      campaign.assignment?.assignmentSendDate &&
                      (sendBlockReason ? (
                        <Tooltip title={sendBlockReason}>
                          <MenuItem disabled>
                            <ListItemIcon>
                              <Schedule fontSize="small" />
                            </ListItemIcon>
                            Schedule
                          </MenuItem>
                        </Tooltip>
                      ) : (
                        <MenuItem onClick={handleScheduleClick}>
                          <ListItemIcon>
                            <Schedule fontSize="small" />
                          </ListItemIcon>
                          Schedule
                        </MenuItem>
                      ))}
                    {[CampaignStateType.Draft].includes(campaign.state) &&
                      campaign.campaignType === CampaignTypeEnum.Survey &&
                      !campaign.autoSendDate &&
                      (sendBlockReason ? (
                        <Tooltip title={sendBlockReason}>
                          <MenuItem disabled>
                            <ListItemIcon>
                              <Send fontSize="small" />
                            </ListItemIcon>
                            Send
                          </MenuItem>
                        </Tooltip>
                      ) : (
                        <MenuItem onClick={handleSendClick}>
                          <ListItemIcon>
                            <Send fontSize="small" />
                          </ListItemIcon>
                          Send
                        </MenuItem>
                      ))}
                    {[CampaignStateType.Draft].includes(campaign.state) &&
                      campaign.campaignType === CampaignTypeEnum.Survey &&
                      campaign.autoSendDate &&
                      (sendBlockReason ? (
                        <Tooltip title={sendBlockReason}>
                          <MenuItem disabled>
                            <ListItemIcon>
                              <Schedule fontSize="small" />
                            </ListItemIcon>
                            Schedule
                          </MenuItem>
                        </Tooltip>
                      ) : (
                        <MenuItem onClick={handleScheduleClick}>
                          <ListItemIcon>
                            <Schedule fontSize="small" />
                          </ListItemIcon>
                          Schedule
                        </MenuItem>
                      ))}
                    {campaign.state === CampaignStateType.AssignmentActive &&
                      !campaign.assignment?.assignmentCloseDate && (
                        <MenuItem onClick={handleAssignmentCloseClick}>
                          <ListItemIcon>
                            <Close fontSize="small" />
                          </ListItemIcon>
                          Close Assignment
                        </MenuItem>
                      )}

                    {[
                      CampaignStateType.AssignmentScheduled,
                      CampaignStateType.Scheduled,
                    ].includes(campaign.state) && (
                      <MenuItem onClick={handleCancelClick}>
                        <ListItemIcon>
                          <Close fontSize="small" />
                        </ListItemIcon>
                        Cancel Schedule
                      </MenuItem>
                    )}
                    {(campaign.state === CampaignStateType.Active ||
                      campaign.state ===
                        CampaignStateType.AssignmentActive) && (
                      <MenuItem onClick={handleReminderClick}>
                        <ListItemIcon>
                          <Send fontSize="small" />
                        </ListItemIcon>
                        Send Reminder
                      </MenuItem>
                    )}
                    {campaign.state !== CampaignStateType.Draft &&
                      campaign.state !==
                        CampaignStateType.AssignmentScheduled &&
                      campaign.state !== CampaignStateType.Closed &&
                      campaign.state !== CampaignStateType.Scheduled && (
                        <MenuItem onClick={handleCloseClick}>
                          <ListItemIcon>
                            <Close fontSize="small" />
                          </ListItemIcon>
                          Close Survey
                        </MenuItem>
                      )}
                    {campaign.state === CampaignStateType.Closed && (
                      <MenuItem onClick={handleExportGradesClick}>
                        <ListItemIcon>
                          <Download fontSize="small" />
                        </ListItemIcon>
                        Export Grades
                      </MenuItem>
                    )}
                    {campaign.state === CampaignStateType.Closed &&
                      campaign.released && (
                        <MenuItem onClick={handleUnreleaseClick}>
                          <ListItemIcon>
                            <Close fontSize="small" />
                          </ListItemIcon>
                          {t(`${tPrefix}.menu.unreleaseResults`)}
                        </MenuItem>
                      )}
                    {campaign.state === CampaignStateType.Closed &&
                      campaign.assignmentEnabled &&
                      campaign.course &&
                      campaign.course.gradePushReady && (
                        <MenuItem
                          onClick={handlePushGradesClick}
                          disabled={isPushingGrades}
                        >
                          <ListItemIcon>
                            {isPushingGrades ? (
                              <CircularProgress size={24} />
                            ) : campaign.gradesSyncedToLms ? (
                              <Check fontSize="small" />
                            ) : (
                              <Grading fontSize="small" />
                            )}
                          </ListItemIcon>
                          Re-sync grades
                        </MenuItem>
                      )}
                    {isDeleteable && (
                      <MenuItem onClick={handleDeleteClick}>
                        <ListItemIcon>
                          <Close fontSize="small" />
                        </ListItemIcon>
                        Delete{" "}
                        {campaign.campaignType === CampaignTypeEnum.Assignment
                          ? "Assignment"
                          : "Survey"}
                      </MenuItem>
                    )}
                    {campaign.state === CampaignStateType.Closed && (
                      <MenuItem onClick={handleReopenClick}>
                        <ListItemIcon>
                          <LockOpen fontSize="small" />
                        </ListItemIcon>
                        Reopen{" "}
                        {campaign.campaignType === CampaignTypeEnum.Assignment
                          ? "Assignment"
                          : "Survey"}
                      </MenuItem>
                    )}
                  </Menu>
                </>
              ) : (
                <>
                  <IconButton
                    onClick={handleActionClick}
                    size="small"
                    sx={{ ml: 2 }}
                    aria-controls={actionMenuOpen ? "campaign-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={actionMenuOpen ? "true" : undefined}
                  >
                    <MoreVert />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={actionMenuOpen}
                    onClose={handleActionClose}
                    onClick={handleActionClose}
                    PaperProps={{
                      elevation: 0,
                      sx: {
                        overflow: "visible",
                        filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                        mt: 1.5,
                        "& .MuiAvatar-root": {
                          width: 32,
                          height: 32,
                          ml: -0.5,
                          mr: 1,
                        },
                        "&:before": {
                          content: '""',
                          display: "block",
                          position: "absolute",
                          top: 0,
                          right: 14,
                          width: 10,
                          height: 10,
                          bgcolor: "background.paper",
                          transform: "translateY(-50%) rotate(45deg)",
                          zIndex: 0,
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    {[
                      CampaignStateType.Draft,
                      CampaignStateType.AssignmentClosed,
                    ].includes(campaign.state) &&
                      ((campaign.campaignType === CampaignTypeEnum.Assignment &&
                        !campaign.assignment?.assignmentSendDate) ||
                        (campaign.campaignType === CampaignTypeEnum.Survey &&
                          !campaign.autoSendDate)) &&
                      (sendBlockReason ? (
                        <Tooltip title={sendBlockReason}>
                          <MenuItem disabled>
                            <ListItemIcon>
                              <Send fontSize="small" />
                            </ListItemIcon>
                            Send
                          </MenuItem>
                        </Tooltip>
                      ) : (
                        <MenuItem onClick={handleSendClick}>
                          <ListItemIcon>
                            <Send fontSize="small" />
                          </ListItemIcon>
                          Send
                        </MenuItem>
                      ))}
                    {[
                      CampaignStateType.Draft,
                      CampaignStateType.AssignmentClosed,
                    ].includes(campaign.state) &&
                      ((campaign.campaignType === CampaignTypeEnum.Assignment &&
                        campaign.assignment?.assignmentSendDate) ||
                        (campaign.campaignType === CampaignTypeEnum.Survey &&
                          campaign.autoSendDate)) &&
                      (sendBlockReason ? (
                        <Tooltip title={sendBlockReason}>
                          <MenuItem disabled>
                            <ListItemIcon>
                              <Schedule fontSize="small" />
                            </ListItemIcon>
                            {campaign.campaignType ===
                            CampaignTypeEnum.Assignment
                              ? "Schedule Assignment"
                              : "Schedule"}
                          </MenuItem>
                        </Tooltip>
                      ) : (
                        <MenuItem onClick={handleScheduleClick}>
                          <ListItemIcon>
                            <Schedule fontSize="small" />
                          </ListItemIcon>
                          {campaign.campaignType === CampaignTypeEnum.Assignment
                            ? "Schedule Assignment"
                            : "Schedule"}
                        </MenuItem>
                      ))}
                    {campaign.state ===
                      CampaignStateType.AssignmentScheduled && (
                      <MenuItem onClick={handleAssignmentActivateClick}>
                        <ListItemIcon>
                          <Send fontSize="small" />
                        </ListItemIcon>
                        Send Assignment Now
                      </MenuItem>
                    )}
                    {campaign.state === CampaignStateType.AssignmentActive && (
                      <MenuItem onClick={handleAssignmentCloseClick}>
                        <ListItemIcon>
                          <Close fontSize="small" />
                        </ListItemIcon>
                        Close Assignment
                      </MenuItem>
                    )}
                    {campaign.state === CampaignStateType.AssignmentClosed && (
                      <MenuItem onClick={handleScheduleReviewClick}>
                        <ListItemIcon>
                          <Schedule fontSize="small" />
                        </ListItemIcon>
                        Schedule Review
                      </MenuItem>
                    )}
                    {[
                      CampaignStateType.AssignmentScheduled,
                      CampaignStateType.Scheduled,
                    ].includes(campaign.state) && (
                      <MenuItem onClick={handleCancelClick}>
                        <ListItemIcon>
                          <Close fontSize="small" />
                        </ListItemIcon>
                        Cancel Schedule
                      </MenuItem>
                    )}
                    {(campaign.state === CampaignStateType.Active ||
                      campaign.state ===
                        CampaignStateType.AssignmentActive) && (
                      <MenuItem onClick={handleReminderClick}>
                        <ListItemIcon>
                          <Send fontSize="small" />
                        </ListItemIcon>
                        Send Reminder
                      </MenuItem>
                    )}
                    {campaign.state !== CampaignStateType.Draft &&
                      campaign.state !==
                        CampaignStateType.AssignmentScheduled &&
                      campaign.state !== CampaignStateType.Closed &&
                      campaign.state !== CampaignStateType.Scheduled && (
                        <MenuItem onClick={handleCloseClick}>
                          <ListItemIcon>
                            <Close fontSize="small" />
                          </ListItemIcon>
                          Close{" "}
                          {campaign.campaignType === CampaignTypeEnum.Assignment
                            ? "Assignment"
                            : "Survey"}
                        </MenuItem>
                      )}
                    {campaign.state !== CampaignStateType.Draft && (
                      <MenuItem
                        component={Link}
                        to={`/results/${campaign.id}//`}
                      >
                        <ListItemIcon>
                          <BarChart fontSize="small" />
                        </ListItemIcon>
                        View Results
                      </MenuItem>
                    )}
                    {campaign.state === CampaignStateType.Closed &&
                      campaign.released === null && (
                        <MenuItem
                          onClick={handleReleaseClick}
                          disabled={isReleasing}
                        >
                          <ListItemIcon>
                            {isReleasing ? (
                              <CircularProgress size={24} />
                            ) : (
                              <Preview />
                            )}
                          </ListItemIcon>
                          {campaign.autoReleaseDate
                            ? "Release Results Now"
                            : "Release Results"}
                        </MenuItem>
                      )}
                    {campaign.state === CampaignStateType.Active && (
                      <MenuItem onClick={handleReminderClick}>
                        <ListItemIcon>
                          <Send fontSize="small" />
                        </ListItemIcon>
                        Send Reminder
                      </MenuItem>
                    )}
                    {campaign.state === CampaignStateType.Scheduled && (
                      <MenuItem onClick={handleCancelClick}>
                        <ListItemIcon>
                          <Close fontSize="small" />
                        </ListItemIcon>
                        Cancel Schedule
                      </MenuItem>
                    )}
                    {campaign.state === CampaignStateType.Closed && (
                      <MenuItem onClick={handleReopenClick}>
                        <ListItemIcon>
                          <LockOpen fontSize="small" />
                        </ListItemIcon>
                        Re-open Survey
                      </MenuItem>
                    )}
                    {campaign.state === CampaignStateType.Closed && (
                      <MenuItem onClick={handleExportGradesClick}>
                        <ListItemIcon>
                          <Download fontSize="small" />
                        </ListItemIcon>
                        Export Grades
                      </MenuItem>
                    )}
                    {isDeleteable && (
                      <MenuItem onClick={handleDeleteClick}>
                        <ListItemIcon>
                          <Close fontSize="small" />
                        </ListItemIcon>
                        Delete{" "}
                        {campaign.campaignType === CampaignTypeEnum.Assignment
                          ? "Assignment"
                          : "Survey"}
                      </MenuItem>
                    )}
                    {campaign.state === CampaignStateType.Closed &&
                      campaign.assignmentEnabled && (
                        <MenuItem
                          onClick={handlePushGradesClick}
                          disabled={isPushingGrades}
                        >
                          <ListItemIcon>
                            {isPushingGrades ? (
                              <CircularProgress size={24} />
                            ) : campaign.gradesSyncedToLms ? (
                              <Check fontSize="small" />
                            ) : (
                              <Grading fontSize="small" />
                            )}
                          </ListItemIcon>
                          {campaign.gradesSyncedToLms
                            ? "Push Grades to LMS Again"
                            : "Push Grades to LMS"}
                        </MenuItem>
                      )}
                    {isDeleteable && (
                      <MenuItem onClick={handleDeleteClick}>
                        <ListItemIcon>
                          <Close fontSize="small" />
                        </ListItemIcon>
                        Delete{" "}
                        {campaign.campaignType === CampaignTypeEnum.Assignment
                          ? "Assignment"
                          : "Survey"}
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </>
          }
          title={
            <TextField
              variant="outlined"
              label={t(`${tPrefix}.label`)}
              inputProps={{ maxLength: 255 }}
              defaultValue={campaign.name}
              fullWidth={true}
              size="small"
              onBlur={(e) => setCampaignTitle(e.target.value.trim())}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                setCampaignTitle(
                  (e.target as unknown as { value: string }).value.trim(),
                );
              }}
            />
          }
        />
        <CardContent>
          {/* Two-column layout for Assignment-type campaigns */}
          {campaign.campaignType === CampaignTypeEnum.Assignment ? (
            <Grid container spacing={3}>
              {/* Left Column - Assignment Details */}
              <Grid item xs={12} xl={6}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Assignment Submission
                  </Typography>
                  <AssignmentDetails
                    campaign={campaign}
                    categories={categories}
                    teams={teams}
                    onChange={onChange}
                    onSetGroupCategory={onSetGroupCategory}
                  />
                </Box>
              </Grid>

              {/* Right Column - Review Configuration */}
              <Grid item xs={12} xl={6}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Submission Review
                  </Typography>
                  <Card variant="outlined">
                    <CardContent>
                      {campaign.state === CampaignStateType.Draft && (
                        <EditContent />
                      )}
                      {campaign.state !== CampaignStateType.Draft && (
                        <ViewContent />
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          ) : (
            /* Single column layout for Survey-type campaigns */
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              {campaign.state === CampaignStateType.Draft && <EditContent />}
              {campaign.state !== CampaignStateType.Draft && <ViewContent />}
              {campaign.state !== CampaignStateType.Draft &&
                campaign.state !== CampaignStateType.Scheduled && (
                  <CampaignProgress responses={responses} />
                )}
            </Box>
          )}
        </CardContent>
      </Card>
      <Teams
        teams={teams}
        campaign={campaign}
        users={users}
        responses={responses}
        onExclude={onExclude}
      />
      <Activity campaign={campaign} />
    </>
  );
};
